// SPDX-License-Identifier: AGPL-3.0-or-later
import { z } from 'zod';
import { CodingSchema } from './codings.js';
import { AttachmentSchema, ExtensionSchema, FhirDateSchema, FhirDateTimeSchema, FhirTimeSchema, QuantitySchema, ReferenceSchema, } from './questionnaire.js';
/**
 * Zod schemas for FHIR R4 `QuestionnaireResponse`. Mirrors the strategy of
 * `questionnaire.ts`: every object uses `.passthrough()` so unknown FHIR
 * fields round-trip unchanged.
 *
 * Reference: https://www.hl7.org/fhir/R4/questionnaireresponse.html
 */
export const QuestionnaireResponseStatusSchema = z.enum([
    'in-progress',
    'completed',
    'amended',
    'entered-in-error',
    'stopped',
]);
/**
 * Upper bounds on the arrays of a QuestionnaireResponse. A response arrives
 * from an unauthenticated patient (POST /api/intake/:token/submit), and zod
 * records one issue per failing array element, so an unbounded array lets a
 * 1 MiB body allocate hundreds of MiB of issues (GHSA-mrhx-c7xc-m3w5). From
 * zod 4.6.6 a `.max()` is checked before any element is parsed, so an
 * oversized array costs one issue.
 *
 * The limits are far above anything a real intake form produces:
 *   - items: one entry per answered question at a level; 1000 is more than
 *     four times the 216 questions of all seeded templates combined.
 *   - answers: one entry per selected value of a repeating question; bounded
 *     in practice by the question's answerOption list.
 *   - extensions and references: a handful in real FHIR; 100 is generous.
 *
 * These bound each array, not the tree: items nest, so the response service
 * also caps the total number of values before it parses at all.
 */
export const MAX_RESPONSE_ITEMS = 1000;
export const MAX_RESPONSE_ANSWERS = 1000;
export const MAX_RESPONSE_EXTENSIONS = 100;
export const MAX_RESPONSE_REFERENCES = 100;
export const QuestionnaireResponseAnswerSchema = z.lazy(() => z
    .object({
    valueBoolean: z.boolean().optional(),
    valueDecimal: z.number().optional(),
    valueInteger: z.number().int().optional(),
    valueDate: FhirDateSchema.optional(),
    valueDateTime: FhirDateTimeSchema.optional(),
    valueTime: FhirTimeSchema.optional(),
    valueString: z.string().optional(),
    valueUri: z.string().optional(),
    valueAttachment: AttachmentSchema.optional(),
    valueCoding: CodingSchema.optional(),
    valueQuantity: QuantitySchema.optional(),
    valueReference: ReferenceSchema.optional(),
    item: z.array(QuestionnaireResponseItemSchema).max(MAX_RESPONSE_ITEMS).optional(),
    extension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
    modifierExtension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
})
    .passthrough());
export const QuestionnaireResponseItemSchema = z.lazy(() => z
    .object({
    linkId: z.string(),
    definition: z.string().optional(),
    text: z.string().optional(),
    answer: z.array(QuestionnaireResponseAnswerSchema).max(MAX_RESPONSE_ANSWERS).optional(),
    item: z.array(QuestionnaireResponseItemSchema).max(MAX_RESPONSE_ITEMS).optional(),
    extension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
    modifierExtension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
})
    .passthrough());
export const QuestionnaireResponseSchema = z
    .object({
    resourceType: z.literal('QuestionnaireResponse'),
    id: z.string().optional(),
    identifier: z.unknown().optional(),
    basedOn: z.array(ReferenceSchema).max(MAX_RESPONSE_REFERENCES).optional(),
    partOf: z.array(ReferenceSchema).max(MAX_RESPONSE_REFERENCES).optional(),
    questionnaire: z.string().optional(),
    status: QuestionnaireResponseStatusSchema,
    subject: ReferenceSchema.optional(),
    encounter: ReferenceSchema.optional(),
    authored: FhirDateTimeSchema.optional(),
    author: ReferenceSchema.optional(),
    source: ReferenceSchema.optional(),
    item: z.array(QuestionnaireResponseItemSchema).max(MAX_RESPONSE_ITEMS).optional(),
    extension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
    modifierExtension: z.array(ExtensionSchema).max(MAX_RESPONSE_EXTENSIONS).optional(),
})
    .passthrough();
//# sourceMappingURL=response.js.map